import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import UserService from '../services/user.service';
import useApi from "../hooks/useApi";
import useAuth from '../hooks/useAuth';
import { PhoneInput } from 'react-international-phone';
import { useTranslation } from 'react-i18next';
import { Button, Container, createTheme, ThemeProvider } from '@mui/material';
import { useNotifications } from '../context/NotificationProvider';
import { mapOtpErrors } from '../features/auth/errorMapping';
import useOtpFlow, { OTP_RESEND_COUNTDOWN_SECONDS } from '../features/auth/useOtpFlow';
import { withSafeUserExtra } from '../utils/userDisplay';
import '../styles/verification-numero.css';
import '../styles/auth-remaster.css';

const theme = createTheme({
  palette: {
    white: {
      main: "#FFFFFF",
      light: "#FFFFFF",
      dark: "#FFFFFF",
      contrastText: "#FFFFFF"
    },
    dark_yellow: {
      main: "#393D44",
      light: "#393D44",
      dark: "#393D44",
      contrastText: "#FAEF9B",
    },
    dark: {
      main: "#393D44",
      light: "#393D44",
      dark: "#393D44",
      contrastText: "#393D44"
    }
  },
});

const maskPhone = (phone = "") => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length <= 4) return phone;
  return `${phone.slice(0, -2).replace(/\d(?=\d{2})/g, "•")}${phone.slice(-2)}`;
};

const getRequestErrorMessage = (error, t) => {
  if (error?.code === "ERR_NETWORK") {
    return t("errors.network");
  }
  if (error?.response?.status === 429) {
    return error?.response?.data?.message || t("errors.retryLater");
  }
  if (error?.response?.status >= 500) {
    return t("errors.server");
  }
  return t("errors.unexpected");
};

const VerificationNumero = () => {
  const { t } = useTranslation('phone');
  const api = useApi();
  const { auth, setAuth } = useAuth();
  const notify = useNotifications();
  const navigate = useNavigate();
  const autoSendRef = useRef(false);
  const otpInputRef = useRef(null);
  const currentPhone = auth?.user?.phone ?? "";
  const [hasSentCode, setHasSentCode] = useState(false);

  const {
    phone,
    setPhone,
    otp,
    setOtp,
    phoneError,
    setPhoneError,
    otpError,
    setOtpError,
    isSubmitting,
    setIsSubmitting,
    isSendingOtp,
    setIsSendingOtp,
    resendCountdown,
    resendDisabled,
    validatePhone,
    validateOtp,
    startCountdown,
    resetOtpError,
    resetPhoneError,
    normalizedPhone,
  } = useOtpFlow({
    initialPhone: currentPhone,
    t,
  });

  const hasPhone = useMemo(() => Boolean(auth?.user?.phone), [auth?.user?.phone]);

  const sendOTP = useCallback(async () => {
    const targetPhone = auth?.user?.phone || normalizedPhone;
    if (!targetPhone) return;

    setIsSendingOtp(true);
    resetOtpError();
    try {
      await UserService.sendOTP(api, { suppressValidationToasts: true });
      setHasSentCode(true);
      startCountdown(OTP_RESEND_COUNTDOWN_SECONDS);
    } catch (error) {
      const { fieldErrors, globalMessage } = mapOtpErrors(error);
      if (fieldErrors.otp) {
        setOtpError(fieldErrors.otp);
      } else if (globalMessage) {
        notify.error(globalMessage);
      } else {
        notify.error(getRequestErrorMessage(error, t));
      }
    } finally {
      setIsSendingOtp(false);
    }
  }, [api, auth?.user?.phone, normalizedPhone, notify, resetOtpError, setIsSendingOtp, setOtpError, startCountdown, t]);

  useEffect(() => {
    if (!hasPhone || autoSendRef.current) return;
    autoSendRef.current = true;
    sendOTP();
  }, [hasPhone, sendOTP]);

  const updateUserPhone = async (event) => {
    event.preventDefault();
    resetPhoneError();
    const validationError = validatePhone(phone);
    if (validationError) return;

    setIsSubmitting(true);
    try {
      await UserService.update(
        auth.user.id,
        { phone: normalizedPhone },
        api,
        { suppressValidationToasts: true }
      );
      const nextAuth = {
        ...auth,
        user: {
          ...auth.user,
          phone: normalizedPhone,
        },
      };
      autoSendRef.current = true;
      setAuth(nextAuth);
      setPhone(normalizedPhone);
      await sendOTP();
    } catch (error) {
      if (error?.response?.status === 422) {
        const { fieldErrors, globalMessage } = mapOtpErrors(error);
        if (fieldErrors.phone) {
          setPhoneError(fieldErrors.phone);
        } else if (globalMessage) {
          setPhoneError(globalMessage);
        }
      } else {
        setPhoneError(getRequestErrorMessage(error, t));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedOtp = otp.replace(/\D/g, "");
    setOtp(normalizedOtp);
    resetOtpError();
    const validationError = validateOtp(normalizedOtp);
    if (validationError) return;

    setIsSubmitting(true);
    try {
      await UserService.verifyOTP({ otp: normalizedOtp }, api, {
        suppressValidationToasts: true,
      });
      setAuth({
        ...auth,
        user: withSafeUserExtra({
          ...auth.user,
          status: 'ACTIVE',
        }),
      });
      navigate('/phone-confirmation');
    } catch (error) {
      if (error?.response?.status === 422) {
        const { fieldErrors, globalMessage } = mapOtpErrors(error);
        setOtpError(fieldErrors.otp || globalMessage || t("errors.otpInvalid"));
      } else {
        setOtpError(getRequestErrorMessage(error, t));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendLabel = t('resendCode');

  const otpDigits = useMemo(
    () => Array.from({ length: 6 }, (_, index) => otp[index] || ""),
    [otp]
  );

  const activeOtpIndex = useMemo(() => {
    const nextEmptyIndex = otpDigits.findIndex((digit) => !digit);
    return nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
  }, [otpDigits]);

  return (
    <>
      <Header />

      <div className="container container-contact verification-numero">
        {!hasPhone ? <h2>{t('verification')}</h2> : null}

        {!hasPhone && (
          <>
            <p>{t('numero')}</p>
            <form onSubmit={updateUserPhone} noValidate>
              <div className="form-text">
                <label>{t('portable')}</label>
                <PhoneInput
                  defaultCountry="ch"
                  type="tel"
                  name="phone"
                  value={phone}
                  disableCountryGuess={true}
                  forceDialCode={true}
                  onChange={setPhone}
                  onBlur={() => validatePhone(phone)}
                  required
                />
                {phoneError ? <small className="auth-field-error">{phoneError}</small> : null}
              </div>
              <div className="form-submit">
                <input
                  type="submit"
                  value={isSubmitting ? t('sendingCode') : t('updatePhone')}
                  disabled={isSubmitting}
                />
              </div>
            </form>
          </>
        )}

        {hasPhone && (
          <>
            <h2 className="verification-numero__title">{t('otpTitle')}</h2>
            <p className="verification-numero__lead">
              {t('otpConfirmation')}<br />{t('otpInstruction')}
            </p>
            <p className="verification-numero__masked-phone">{maskPhone(auth.user?.phone || phone)}</p>
            {hasSentCode ? (
              <div className="verification-numero__sent-badge">{t('otpSentBadge')}</div>
            ) : null}
            <form onSubmit={handleSubmit} noValidate>
              <div className="verification-numero__otp-group">
                <label className="verification-numero__otp-label" htmlFor="otp">{t('otpLabel')}</label>
                <div
                  className="verification-numero__otp-boxes"
                  onClick={() => otpInputRef.current?.focus()}
                  role="presentation"
                >
                  <input
                    ref={otpInputRef}
                    className="verification-numero__otp-hidden-input"
                    type="text"
                    value={otp}
                    onChange={(event) => {
                      setOtp(event.target.value.replace(/\D/g, "").slice(0, 6));
                      resetOtpError();
                    }}
                    onBlur={() => validateOtp(otp)}
                    name="otp"
                    id="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    aria-label={t('otpLabel')}
                  />
                  {otpDigits.map((digit, index) => (
                    <div
                      key={index}
                      className={`verification-numero__otp-box ${activeOtpIndex === index ? 'is-active' : ''} ${digit ? 'has-value' : ''}`}
                    >
                      {digit}
                    </div>
                  ))}
                </div>
                {otpError ? <small className="auth-field-error">{otpError}</small> : null}
              </div>
              <Container className="verification-numero__actions" sx={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'nowrap', padding: '40px 0 !important' }}>
                <ThemeProvider theme={theme}>
                  <Button
                    variant="outlined"
                    color='dark'
                    type="button"
                    onClick={sendOTP}
                    disabled={resendDisabled}
                    sx={{ textTransform: 'none', borderRadius: '15px', flex: '1 1 0', width: 'auto', maxWidth: '180px', minWidth: 0, minHeight: '57px' }}
                  >
                    {isSendingOtp ? t('sendingCode') : resendLabel}
                  </Button>
                  <Button
                    variant="contained"
                    color='dark_yellow'
                    type="submit"
                    disabled={isSubmitting}
                    sx={{ textTransform: 'none', borderRadius: '15px', flex: '1 1 0', width: 'auto', maxWidth: '180px', minWidth: 0, minHeight: '57px' }}
                  >
                    {isSubmitting ? t('verifying') : t('verify')}
                  </Button>
                </ThemeProvider>
              </Container>
              {resendCountdown > 0 ? (
                <p className="verification-numero__countdown">
                  {t('resendAvailableIn', { seconds: resendCountdown })}
                </p>
              ) : null}
            </form>
          </>
        )}
      </div>
    </>
  );
};

export default VerificationNumero;
